import { Controller } from "@hotwired/stimulus"
import EditorJS from '@editorjs/editorjs'
import Header from '@editorjs/header'
import List from '@editorjs/list'
import Paragraph from '@editorjs/paragraph'
import Quote from '@editorjs/quote'
import Code from '@editorjs/code'
import Link from '@editorjs/link'
import Marker from '@editorjs/marker'
import Delimiter from '@editorjs/delimiter'
import InlineCode from '@editorjs/inline-code'
import Underline from '@editorjs/underline'
import ImageTool from '@editorjs/image'
import GiphyTool from './tools/giphy_tool.js'

export default class extends Controller {
  static targets = ["editor", "input"]
  static values = { 
    placeholder: String,
    readonly: Boolean,
    data: Object
  }

  connect() {
    this.initializeEditor()
  }

  disconnect() {
    if (this.editor) {
      this.editor.destroy()
      this.editor = null
    }
  }

  async initializeEditor() {
    try {
      // Parse the data value safely
      let editorData = { blocks: [] }
      if (this.dataValue) {
        try {
          if (typeof this.dataValue === 'string') {
            editorData = JSON.parse(this.dataValue)
          } else {
            editorData = this.dataValue
          }
        } catch (parseError) {
          console.warn('Failed to parse editor data, using empty content:', parseError)
          editorData = { blocks: [] }
        }
      }

      this.editor = new EditorJS({
        holder: this.editorTarget,
        placeholder: this.placeholderValue || 'Start writing...',
        readOnly: this.readonlyValue || false,
        data: editorData,
        minHeight: 300,
        defaultBlock: 'paragraph', // Set default block type
        
        tools: {
          header: {
            class: Header,
            config: {
              levels: [1, 2, 3, 4],
              defaultLevel: 2
            }
          },
          paragraph: {
            class: Paragraph,
            inlineToolbar: true
          },
          list: {
            class: List,
            inlineToolbar: true,
            config: {
              defaultStyle: 'unordered'
            }
          },
          quote: {
            class: Quote,
            inlineToolbar: true,
            config: {
              quotePlaceholder: 'Enter a quote',
              captionPlaceholder: 'Quote author'
            }
          },
          code: {
            class: Code,
            config: {
              placeholder: 'Enter code...'
            }
          },
          link: {
            class: Link,
            config: {
              endpoint: '/api/fetch-url' // You'll need to implement this endpoint
            }
          },
          image: {
            class: ImageTool,
            config: {
              uploader: {
                uploadByFile: (file) => {
                  return this.uploadImageFile(file)
                }
              },
              captionPlaceholder: 'Enter caption (optional)'
            }
                      },
            giphy: {
              class: GiphyTool
            },
            marker: {
              class: Marker
          },
          delimiter: {
            class: Delimiter
          },
          inlineCode: {
            class: InlineCode
          },
          underline: {
            class: Underline
          }
        },
        onChange: () => {
          this.saveData()
        }
      })

      await this.editor.isReady
      console.log('Editor.js is ready to work!')
      
    } catch (error) {
      console.error('Editor.js initialization failed:', error)
    }
  }

  async saveData() {
    if (!this.editor) return
    
    try {
      const outputData = await this.editor.save()
      if (this.hasInputTarget) {
        this.inputTarget.value = JSON.stringify(outputData)
        this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }))
      }
    } catch (error) {
      console.error('Error saving editor data:', error)
    }
  }

  // Method to programmatically set data
  async setData(data) {
    if (!this.editor) return
    
    try {
      await this.editor.render(data)
    } catch (error) {
      console.error('Error setting editor data:', error)
    }
  }

  // Method to get current data
  async getData() {
    if (!this.editor) return null
    
    try {
      return await this.editor.save()
    } catch (error) {
      console.error('Error getting editor data:', error)
      return null
    }
  }

  // Method to upload image files
  async uploadImageFile(file) {
    try {
      const formData = new FormData()
      formData.append('image', file)
      
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      
      const response = await fetch('/guides/upload_image', {
        method: 'POST',
        body: formData,
        headers: {
          'X-CSRF-Token': csrfToken
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const result = await response.json()
      
      if (result.success && result.file) {
        return {
          success: 1,
          file: {
            url: result.file.url,
            name: result.file.name || file.name,
            size: result.file.size || file.size
          }
        }
      } else {
        throw new Error(result.error || 'Upload failed')
      }
    } catch (error) {
      console.error('Image upload error:', error)
      return {
        success: 0,
        error: error.message || 'Image upload failed'
      }
    }
  }
} 